import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface LifeEvent {
  id: string;
  title: string;
  description?: string;
  event_date: string;
  user_id: number;
  created_at: string;
  created_by: string;
}

export interface CreateLifeEventData {
  id?: string;
  title: string;
  description?: string;
  event_date: string;
}

export interface UpdateLifeEventData {
  title?: string;
  description?: string;
  event_date?: string;
}

export interface LifeEventFilters {
  start_date?: string;
  end_date?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

class LifeEventsService {
  // Get all life events
  async getLifeEvents(filters?: LifeEventFilters): Promise<{ success: boolean; events?: LifeEvent[]; count?: number; message?: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.start_date) queryParams.append('start_date', filters.start_date);
      if (filters?.end_date) queryParams.append('end_date', filters.end_date);
      if (filters?.search) queryParams.append('search', filters.search);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/life-events${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching life events:', error);
      throw error;
    }
  }

  // Get life event by ID
  async getLifeEventById(id: string): Promise<{ success: boolean; event?: LifeEvent; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/life-events/${id}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching life event:', error);
      throw error;
    }
  }

  // Create a new life event
  async createLifeEvent(data: CreateLifeEventData): Promise<{ success: boolean; id?: string; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/life-events', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating life event:', error);
      throw error;
    }
  }

  // Update a life event
  async updateLifeEvent(id: string, data: UpdateLifeEventData): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/life-events/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating life event:', error);
      throw error;
    }
  }

  // Delete a life event
  async deleteLifeEvent(id: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/life-events/${id}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting life event:', error);
      throw error;
    }
  }
}

export const lifeEventsService = new LifeEventsService();

